import { newUuid } from "@/utils/uuid";

const TELEMETRY_URL = "/api/t";

export type TelemetryEventType = "page_view" | "request_error" | "error" | "log" | "track";

export type TelemetryEvent = {
  type: TelemetryEventType;
  recordedAt: string;
  data: Record<string, unknown>;
};

export type TelemetryBatch = {
  correlationId: string;
  sha: string;
  events: TelemetryEvent[];
};

export type TelemetryContext = { correlationId: string; sha: string };

export type TelemetryFloodState = { minute: number; count: number };

export type TelemetryState = { context: TelemetryContext; flood: TelemetryFloodState };

export type RecordTelemetryEvent = {
  type: "telemetry";
  eventType: TelemetryEventType;
  data: Record<string, unknown>;
};

export const initialTelemetryFloodState: TelemetryFloodState = { minute: -1, count: 0 };

export function initialTelemetryState(context: TelemetryContext): TelemetryState {
  return { context, flood: initialTelemetryFloodState };
}

export function newCorrelationId() {
  return newUuid();
}

export const telemetryEvent = (
  eventType: TelemetryEventType,
  data: Record<string, unknown>,
): RecordTelemetryEvent => ({ type: "telemetry", eventType, data });

const MAX_EVENTS_PER_MINUTE = 100;
const MAX_EVENT_BYTES = 60_000;
const MILLIS_PER_MINUTE = 60_000;

export function toTelemetryBatch(
  state: TelemetryState,
  event: RecordTelemetryEvent,
  now: Date,
  newCorrelationId: () => string,
): [TelemetryState, TelemetryBatch | undefined] {
  const context =
    event.eventType === "page_view"
      ? { ...state.context, correlationId: newCorrelationId() }
      : state.context;
  const minute = Math.floor(now.getTime() / MILLIS_PER_MINUTE);
  const count = state.flood.minute === minute ? state.flood.count : 0;
  if (count >= MAX_EVENTS_PER_MINUTE) {
    return [{ context, flood: { minute, count } }, undefined];
  }
  const wireEvent = limitEventSize({
    type: event.eventType,
    recordedAt: now.toISOString(),
    data: event.data,
  });
  return [
    { context, flood: { minute, count: count + 1 } },
    { ...context, events: [wireEvent] },
  ];
}

function limitEventSize(event: TelemetryEvent): TelemetryEvent {
  const bytes = new TextEncoder().encode(JSON.stringify(event)).length;
  return bytes > MAX_EVENT_BYTES ? { ...event, data: { truncated: true } } : event;
}

function sendTelemetry(batch: TelemetryBatch) {
  navigator.sendBeacon(TELEMETRY_URL, new Blob([JSON.stringify(batch)], { type: "text/plain" }));
}

function isSendingEnabled(): boolean {
  return import.meta.env.MODE !== "test" && sessionStorage.getItem("telemetry") !== "off";
}

export function handleTelemetryEvent(
  state: TelemetryState,
  event: RecordTelemetryEvent,
): TelemetryState {
  const [next, batch] = toTelemetryBatch(state, event, new Date(), newCorrelationId);
  if (batch && isSendingEnabled()) {
    sendTelemetry(batch);
  }
  return next;
}
